<script setup lang="ts">
import { compareLocalDate, formatLocalDate, type LocalDate, parseLocalDate } from '@taler/domain'
import { ActionButton, StatusTag } from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CreateExportInput, ExportFormat } from '../internal/types.ts'

const props = defineProps<{
  format: ExportFormat
  defaultFrom: LocalDate
  defaultUntil: LocalDate
  isCreating: boolean
  error: Error | null
}>()
const emit = defineEmits<{ submit: [input: CreateExportInput] }>()
const { t } = useI18n()

const fromDate = ref(formatLocalDate(props.defaultFrom))
const untilDate = ref(formatLocalDate(props.defaultUntil))

const isRangeValid = computed(() => {
  try {
    return compareLocalDate(parseLocalDate(fromDate.value), parseLocalDate(untilDate.value)) <= 0
  } catch {
    return false
  }
})

function onSubmit(): void {
  if (!isRangeValid.value) return
  emit('submit', {
    format: props.format,
    fromDate: parseLocalDate(fromDate.value),
    untilDate: parseLocalDate(untilDate.value),
  })
}
</script>

<template>
  <form
    class="flex flex-col gap-[var(--space-group)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-[var(--space-block)] bg-[color:var(--color-surface)]"
    @submit.prevent="onSubmit"
  >
    <div class="flex flex-wrap items-end gap-[var(--space-group)]">
      <template v-if="props.format === 'CSV'">
        <label
          class="flex flex-col gap-[var(--space-hair)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
        >
          {{ t('exports.fromDate') }}
          <input
            v-model="fromDate"
            type="date"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
          />
        </label>
        <label
          class="flex flex-col gap-[var(--space-hair)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
        >
          {{ t('exports.untilDate') }}
          <input
            v-model="untilDate"
            type="date"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
          />
        </label>
      </template>
      <ActionButton type="submit" :disabled="isCreating || !isRangeValid">
        {{ isCreating ? t('exports.requesting') : t('exports.request') }}
      </ActionButton>
    </div>
    <p
      v-if="props.format === 'CSV' && !isRangeValid"
      role="alert"
      class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
    >
      {{ t('exports.invalidRange') }}
    </p>
    <StatusTag v-if="error" tone="danger">{{ t('exports.createError') }}</StatusTag>
  </form>
</template>
